import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { PrimeNG } from 'primeng/config';
import { DividerModule } from 'primeng/divider';
import {
  FileRemoveEvent,
  FileUpload,
  FileUploadHandlerEvent,
} from 'primeng/fileupload';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';
import { StorageService } from '../../_services/storage.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { v4 as uuidv4 } from 'uuid';
import { Card } from 'primeng/card';
import { RippleModule } from 'primeng/ripple';
import { catchError, finalize, of, Subject, takeUntil } from 'rxjs';

export interface DocumentProps extends FormlyFieldProps {
  emptyLabel: string;
  uploadButtonLabel: string;
  deleteButtonLabel: string;
  chooseButtonLabel: string;
  chooseButtonSeverity?: string;
  chooseButtonOutlined?: boolean;
  downloadButtonLabel: string;
  instructionButtonLabel?: string;
  multiple?: boolean;
  maxFileSize?: number;
  accept?: string;
  modello?: any;
  istruzioni?: any;
  disabled?: boolean;
  drag?: boolean;
  fullWidthChooseButton?: boolean;
}

@Component({
  selector: 'formly-field-document',
  standalone: true,
  imports: [
    FileUpload,
    CommonModule,
    Button,
    ...FORM_IMPORTS,
    TranslatePipe,
    DividerModule,
    ProgressSpinnerModule,
    Card,
    RippleModule,
  ],
  template: `
    <p-fileupload
      #fileUpload
      customUpload="true"
      (uploadHandler)="onUploadHandler($event)"
      [multiple]="props.multiple ?? false"
      [maxFileSize]="props.maxFileSize ?? 10 * 1024 * 1024"
      [mode]="'advanced'"
      auto="true"
      [accept]="props.accept"
      [disabled]="props.disabled ?? false"
    >
      <ng-template
        #header
        let-files
        let-chooseCallback="chooseCallback"
        let-clearCallback="clearCallback"
        let-uploadCallback="uploadCallback"
      >
        <div class="flex flex-col flex-wrap gap-1 w-full">
          <div class="flex items-baseline flex-wrap">
            <label
              class="ml-(--p-inputtext-padding-x)"
              [innerHTML]="props.label"
            >
            </label>
            @if (displayError) {
              <small
                class="text-(--p-form-field-invalid-placeholder-color) italic ml-(--p-inputtext-padding-x)"
              >
                <formly-validation-message
                  class="ui-message-text"
                  [field]="field"
                ></formly-validation-message>
              </small>
            }
          </div>

          <!-- <div class="ng-star-inserted">{{ props.label }}</div> -->

          <div class="flex flex-wrap justify-between gap-2">
            @if (props.drag == undefined || props.drag == false) {
              <div
                class="flex gap-2 items-end"
                [ngClass]="{
                  'w-full': props.fullWidthChooseButton === true,
                  'document-full-width-choose':
                    props.fullWidthChooseButton === true,
                }"
              >
                <p-button
                  raised
                  glass
                  inset-shadow
                  (onClick)="choose($event, chooseCallback)"
                  [severity]="$any(props.chooseButtonSeverity)"
                  icon="fa-solid fa-upload"
                  [outlined]="props.chooseButtonOutlined ?? true"
                  size="small"
                  [disabled]="props.disabled ?? false"
                  [fluid]="props.fullWidthChooseButton === true"
                  [style]="
                    props.fullWidthChooseButton === true
                      ? { width: '100%' }
                      : null
                  "
                  [label]="
                    props.chooseButtonLabel ||
                      'formly.type.document.chooseButton' | translate
                  "
                />
              </div>
            }
            <div class="flex gap-2">
              @if (props.modello) {
                <p-button
                  class="self-end"
                  (onClick)="downloadFile(props.modello)"
                  icon="fa-solid fa-download"
                  [variant]="'text'"
                  size="small"
                  severity="help"
                  [label]="
                    props.downloadButtonLabel ||
                      'formly.type.document.downloadButton' | translate
                  "
                />
              }
              @if (props.istruzioni) {
                <p-button
                  class="self-end"
                  (onClick)="downloadFile(props.istruzioni)"
                  icon="fa-solid fa-info"
                  [variant]="'text'"
                  size="small"
                  severity="info"
                  [label]="
                    props.instructionButtonLabel ||
                      'formly.type.document.instrucrionButton' | translate
                  "
                />
              }
            </div>
          </div>
        </div>
      </ng-template>
      <ng-template #empty>
        @if (props.drag === true) {
          <p-card
            class="w-full cursor-pointer border-2 border-dashed"
            [ngClass]="{
              'opacity-50 pointer-events-none': props.disabled,
            }"
            (click)="!props.disabled && fileUpload.choose()"
          >
            <div class="flex items-center justify-center flex-col gap-4 py-6">
              <p-button
                styleClass="p-button-xxxl"
                rounded
                icon="fa-solid fa-upload"
                severity="secondary"
                [outlined]="true"
                [disabled]="props.disabled ?? false"
                (onClick)="onChooseClick($event)"
              />

              <div>
                <div
                  class=""
                  [innerHTML]="
                    props.chooseButtonLabel ||
                    ('formly.type.document.chooseButton' | translate)
                  "
                ></div>
                <small
                  class="italic text-gray-400"
                  [innerHTML]="'formly.type.document.dragMessage' | translate"
                >
                </small>
              </div>
            </div>
          </p-card>
        }
      </ng-template>
      <ng-template #file></ng-template>

      <ng-template
        #content
        let-files
        let-progress
        let-uploadedFiles="uploadedFiles"
        let-removeUploadedFileCallback="removeUploadedFileCallback"
      >
        @if (formControl.value == 'loading') {
          <p-progress-spinner
            ariaLabel="loading"
            [style]="{ width: '40px', height: '40px' }"
          />
        } @else if (formControl.value && formControl.value.length > 0) {
          <div class="flex flex-col gap-2 ml-4">
            @for (
              file of formControl.value;
              track file?.old?.unique ?? file?.unique ?? $index;
              let i = $index
            ) {
              <div class="flex items-center justify-between w-full mt-1">
                <div
                  class="flex gap-2 items-center"
                  (click)="open(file?.file ?? file)"
                >
                  @if (file?.objectURL) {
                    <img
                      role="presentation"
                      [alt]="file?.name ?? file?.file?.name"
                      [src]="file.objectURL"
                      width="50"
                      height="100"
                    />
                  } @else {
                    <i class="fa-regular fa-file-lines text-4xl"></i>
                  }
                  <div class="flex flex-col">
                    <div
                      class="paragraph font-bold  text-ellipsis whitespace-nowrap overflow-hidden"
                    >
                      {{ file?.name ?? file?.file?.name }}
                    </div>
                    <div class="text-xs italic">
                      {{ getFormattedSize(file) }}
                    </div>
                  </div>
                </div>
                <div class="flex">
                  <p-button
                    icon="fas fa-eye"
                    [rounded]="true"
                    [text]="true"
                    severity="success"
                    (click)="open(file?.file ?? file)"
                  />
                  @if (isImage(file)) {
                    <p-button
                      icon="fas fa-file-pdf"
                      [rounded]="true"
                      [text]="true"
                      severity="secondary"
                      [attr.title]="'formly.type.document.scaricaPdf' | translate"
                      (click)="downloadAsPdf(file)"
                    />
                  }
                  @if (!props.disabled) {
                    <p-button
                      icon="fas fa-times"
                      [rounded]="true"
                      (click)="removeFile(file)"
                      [text]="true"
                      severity="danger"
                    />
                  }
                </div>
              </div>
              <p-divider></p-divider>
            }
          </div>
        }
      </ng-template>
    </p-fileupload>
  `,
  styles: `
    :host ::ng-deep {
      .p-fileupload-advanced {
        border: 0;
        background: transparent;
        color: inherit;

        .p-fileupload-header {
          padding: 0;
          color: inherit;
        }

        .p-fileupload-highlight {
          .p-card {
            border-color: var(--p-button-primary-background) !important;
          }
        }

        .p-fileupload-content.p-fileupload-highlight {
          border: 0 !important;
          box-shadow: none !important;
          background: transparent !important;
        }

        .p-fileupload-content {
          border: 0 !important;
          box-shadow: none !important;
          padding: 0;

          p-progressbar {
            display: none;
          }

          p-message {
            margin-top: 0.5rem;
          }
        }
      }

      .document-full-width-choose {
        width: 100%;
      }

      .document-full-width-choose p-button {
        width: 100%;
        display: block;
      }

      .document-full-width-choose .p-button {
        width: 100% !important;
      }
    }
  `,
})
export class DocumentFieldType
  extends FieldType<FieldTypeConfig<DocumentProps>>
  implements AfterViewInit, OnDestroy
{
  private destroy$ = new Subject<void>();
  private sizeCache = new Map<string, number>();
  private sizeRequestsInFlight = new Set<string>();
  private nativeFileInput: HTMLInputElement | null = null;

  private readonly nativeInputListener = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const maxSize = this.props.maxFileSize ?? 10 * 1024 * 1024;
    const files = input.files ? Array.from(input.files) : [];
    if (files.some((file) => file.size > maxSize)) {
      this.formControl.setErrors({
        ...(this.formControl.errors ?? {}),
        maxFileSize: this.formatSize(maxSize),
      });
      this.formControl.markAsTouched();
      this.formControl.markAsDirty();
    } else {
      this.clearMaxFileSizeError();
    }
  };

  ngAfterViewInit() {
    this.nativeFileInput =
      this.fileUpload?.advancedFileInput?.nativeElement ?? null;
    this.nativeFileInput?.addEventListener('change', this.nativeInputListener, { capture: true });

    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (Array.isArray(value)) {
          const sanitizedValue = value.filter((file: any) => !!file);
          if (sanitizedValue.length !== value.length) {
            this.formControl.setValue(sanitizedValue, { emitEvent: false });
            value = sanitizedValue;
          }
        }

        const isEmpty =
          value == null || (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          this.fileUpload?.clear(); // ✅ reset UI PrimeNG (torna drag&drop)
        }
      });
  }

  ngOnDestroy() {
    this.nativeFileInput?.removeEventListener('change', this.nativeInputListener, { capture: true });
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearMaxFileSizeError() {
    if (!this.formControl.errors?.['maxFileSize']) return;
    const { maxFileSize, ...rest } = this.formControl.errors;
    this.formControl.setErrors(Object.keys(rest).length > 0 ? rest : null);
  }
  uploadedFiles: any[] = [];

  @ViewChild('fileUpload') fileUpload!: FileUpload;

  private readonly _storage = inject(StorageService);

  constructor(private config: PrimeNG) {
    super();
  }

  get displayError(): boolean {
    const submitted = !!this.field.options?.parentForm?.submitted;
    const parentInteraction = this.hasParentInteraction(this.formControl);
    return (
      this.showError ||
      (this.formControl.invalid &&
        (this.formControl.touched ||
          this.formControl.dirty ||
          parentInteraction ||
          submitted))
    );
  }

  private hasParentInteraction(control: AbstractControl | null): boolean {
    let current = control?.parent ?? null;
    while (current) {
      if (current.touched || current.dirty) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  choose(event: any, callback: any) {
    callback();
  }

  uploadEvent(callback: any) {
    callback();
  }

  onChooseClick(event: Event) {
    event.stopPropagation();
    this.fileUpload.choose();
  }

  onUploadHandler(event: FileUploadHandlerEvent) {
    event.files
      .filter((file: File | null | undefined) => !!file)
      .forEach((file: File) => {
        if (!(file as any).unique) {
          (file as any).unique = uuidv4();
        }
      });

    this.clearMaxFileSizeError();
    this.formControl.setValue(
      this.props.multiple ? event.files : [event.files[0]],
    );
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity();
  }

  removeFile(removedFile: any) {
    const currentFiles = (this.formControl.value ?? []).filter(
      (file: any) => !!file,
    );

    const updatedFiles = currentFiles.filter(
      (f: any) =>
        (f?.old?.unique ?? f.unique) !==
        (removedFile?.old?.unique ?? removedFile.unique),
    );

    // 1️⃣ aggiorno FormControl
    this.formControl.setValue(updatedFiles);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity();

    // 2️⃣ aggiorno lo stato interno di PrimeNG
    this.fileUpload.files = this.fileUpload.files.filter(
      (f: any) => f.unique !== removedFile.unique,
    );

    // 3️⃣ Se non rimane nulla, reset completo per far tornare l'empty template
    if (updatedFiles.length === 0) {
      this.clearMaxFileSizeError();
      this.fileUpload.clear();
    }
  }

  formatSize(bytes: number) {
    const numericBytes = Number(bytes);
    if (!Number.isFinite(numericBytes) || numericBytes < 0) {
      return '';
    }

    const k = 1024;
    const dm = 2;
    const sizes = this.config.translation.fileSizeTypes;
    if (numericBytes === 0) {
      return `0 ${sizes![0]}`;
    }

    const i = Math.floor(Math.log(numericBytes) / Math.log(k));
    const formattedSize = parseFloat(
      (numericBytes / Math.pow(k, i)).toFixed(dm),
    );

    return `${formattedSize} ${sizes![i]}`;
  }

  getFormattedSize(file: any): string {
    const knownSize = this.resolveFileSize(file);
    if (knownSize != null) {
      return this.formatSize(knownSize);
    }

    const ref = this.resolveStoredFileRef(file);
    if (!ref) {
      return '';
    }

    const cachedSize = this.sizeCache.get(ref.id);
    if (cachedSize != null) {
      return this.formatSize(cachedSize);
    }

    this.prefetchFileSize(ref.id, ref.name);
    return '';
  }

  private resolveFileSize(file: any): number | null {
    const candidates = [
      file?.size,
      file?.file?.size,
      file?.old?.size,
      file?.old?.file?.size,
      file?.old?.file?.file?.size,
    ];

    for (const candidate of candidates) {
      const numericSize = Number(candidate);
      if (Number.isFinite(numericSize) && numericSize >= 0) {
        return numericSize;
      }
    }

    return null;
  }

  private prefetchFileSize(id: string, name?: string) {
    if (this.sizeRequestsInFlight.has(id)) {
      return;
    }

    this.sizeRequestsInFlight.add(id);
    const safeName = name?.trim() || `document-${id}`;

    this._storage
      .getFile(id, safeName)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
        finalize(() => this.sizeRequestsInFlight.delete(id)),
      )
      .subscribe((res) => {
        const size = Number((res?.file as Blob | undefined)?.size);
        if (Number.isFinite(size) && size >= 0) {
          this.sizeCache.set(id, size);
        }
      });
  }

  open(file: any) {
    if (!file) {
      return;
    }

    if (file instanceof Blob) {
      this.openBlob(file);
      return;
    }

    if (typeof file === 'string') {
      window.open(file, '_blank');
      return;
    }

    const resolvedRef = this.resolveStoredFileRef(file);
    if (resolvedRef) {
      this.openStoredFile(resolvedRef.id, resolvedRef.name);
      return;
    }

    console.warn('[DocumentFieldType] open(): formato file non supportato', file);
  }

  private resolveStoredFileRef(
    file: any,
  ): { id: string; name?: string } | null {
    const directId = file?.id ?? file?.uuid;
    const directName = file?.name;

    if (directId) {
      return {
        id: String(directId),
        ...(directName != null ? { name: String(directName) } : {}),
      };
    }

    const nestedId = file?.file?.id ?? file?.old?.file?.id ?? file?.old?.id;
    const nestedName = file?.file?.name ?? file?.old?.file?.name ?? file?.name;

    if (nestedId) {
      return {
        id: String(nestedId),
        ...(nestedName != null ? { name: String(nestedName) } : {}),
      };
    }

    return null;
  }

  private openBlob(blob: Blob, targetWindow?: Window | null) {
    const fileURL = URL.createObjectURL(blob);

    if (targetWindow) {
      targetWindow.location.href = fileURL;
    } else {
      window.open(fileURL, '_blank');
    }

    // Lasciamo il tempo al browser di aprire il file, poi ripuliamo la URL.
    setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
  }

  private openStoredFile(id: string, name?: string) {
    const targetWindow = window.open('', '_blank');
    const safeName = name?.trim() || `document-${id}`;

    this._storage
      .getFile(id, safeName)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        const blob = res?.file as Blob | undefined;

        if (!blob) {
          targetWindow?.close();
          return;
        }

        this.openBlob(blob, targetWindow);
      });
  }

  downloadFile(file: any) {
    // Caso 1: file è una stringa → redirect
    if (typeof file === 'string') {
      window.open(file, '_blank');
      return;
    }

    // Caso 2: file su storage. I template di documenti_richiesti sono salvati come { id, name },
    // alcuni form li convertono in { uuid, name }: li accettiamo entrambi.
    const ref = this.resolveStoredFileRef(file);
    if (!ref) {
      console.warn('[DocumentFieldType] downloadFile(): formato file non supportato', file);
      return;
    }

    const name = ref.name?.trim() || `document-${ref.id}`;
    this._storage
      .getFile(ref.id, name)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        const blob = res?.file;
        if (!blob) return;

        this.saveBlob(blob, name);
      });
  }

  private saveBlob(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    // alcuni browser ignorano il click su un link che non è nel DOM
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Come in openBlob: revocare subito la URL può interrompere il download su alcuni browser.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  isImage(file: any): boolean {
    const f = file?.file ?? file;
    return (
      this._storage.isImage({ name: f?.name ?? file?.name }) ||
      (f instanceof Blob && f.type.startsWith('image/'))
    );
  }

  downloadAsPdf(file: any) {
    const f = file?.file ?? file;
    const name: string = f?.name ?? file?.name ?? 'documento';

    // File locale non ancora caricato
    if (f instanceof Blob) {
      void this._storage.blobImmagineToPdf(f, name);
      return;
    }

    const ref = this.resolveStoredFileRef(file);
    if (ref) {
      this._storage.scaricaComePdf({ id: ref.id, name: ref.name ?? name });
    }
  }

  private fileCache = new Map<File, string>(); // Cache per evitare ricaricamenti

  render(file: File): string {
    if (this.fileCache.has(file)) {
      return this.fileCache.get(file)!; // Se già caricato, restituisci il valore
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      this.fileCache.set(file, reader.result as string); // Memorizza il valore nella cache
    };

    return ''; // Restituisce una stringa vuota finché l'immagine non è caricata
  }
}
